import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area, LineChart, Line, ComposedChart
} from 'recharts';
import { grcService } from '../../services/grcService';
import { Framework, DashboardData } from '../../types/grc';
import { useI18n } from '../../context/I18nContext';
import {
  BarChart3, PieChart as PieChartIcon, Activity, Download, RefreshCw,
  TrendingUp, Shield, CheckCircle, AlertTriangle, XCircle, Target, ExternalLink
} from 'lucide-react';

const COLORS = {
  primary: '#6366f1',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  muted: '#6b7280',
  chart: ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6']
};

interface FrameworkAnalytics {
  framework: Framework;
  dashboard: DashboardData | null;
}

interface GRCAnalyticsProps {
  onNavigate?: (section: string, filters?: any) => void;
  onFrameworkSelect?: (framework: Framework) => void;
  selectedFramework?: Framework | null;
}

export const GRCAnalytics: React.FC<GRCAnalyticsProps> = ({ onNavigate, onFrameworkSelect, selectedFramework }) => {
  const { t } = useI18n();
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [analyticsData, setAnalyticsData] = useState<FrameworkAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState<'overview' | 'comparison' | 'trend'>('overview');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const fws = await grcService.getFrameworksWithStatus();
      setFrameworks(fws);

      const analyticsPromises = fws.filter(f => f.total_controls > 0 || f.wizard_completed).map(async fw => {
        const dashboard = await grcService.getFrameworkDashboard(fw.id);
        return { framework: fw, dashboard };
      });

      const results = await Promise.all(analyticsPromises);
      setAnalyticsData(results);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
    setLoading(false);
  };

  // Aggregated compliance score across all frameworks
  const overallCompliance = useMemo(() => {
    if (analyticsData.length === 0) return 0;
    const total = analyticsData.reduce((sum, a) => {
      const score = Number(a.dashboard?.stats?.compliance_score) || 0;
      return sum + score;
    }, 0);
    return Math.round(total / analyticsData.length);
  }, [analyticsData]);

  // Framework comparison data
  const comparisonData = useMemo(() => {
    return analyticsData.map(a => ({
      name: a.framework.code,
      fullName: a.framework.name,
      total: Number(a.dashboard?.stats?.total) || 0,
      applicable: Number(a.dashboard?.status?.applicable_controls) || 0,
      implemented: Number(a.dashboard?.stats?.compliant) || 0,
      partial: Number(a.dashboard?.status?.partial_controls) || 0,
      notApplicable: Number(a.dashboard?.status?.not_applicable_controls) || 0,
      compliance: Number(a.dashboard?.stats?.compliance_score) || 0
    }));
  }, [analyticsData]);

  // Implementation status breakdown
  const implementationData = useMemo(() => {
    const totals = {
      implemented: 0,
      in_progress: 0,
      not_started: 0,
      not_applicable: 0
    };

    analyticsData.forEach(a => {
      if (a.dashboard?.stats) {
        totals.implemented += Number(a.dashboard.stats?.compliant) || 0;
        totals.in_progress += Number(a.dashboard.status?.partial_controls) || 0;
        totals.not_started += (Number(a.dashboard.status?.applicable_controls) || 0) - 
          (Number(a.dashboard.stats?.compliant) || 0) - 
          (Number(a.dashboard.status?.partial_controls) || 0);
        totals.not_applicable += Number(a.dashboard.status?.not_applicable_controls) || 0;
      }
    });

    return [
      { name: t.analytics.implemented, value: totals.implemented, color: COLORS.success },
      { name: t.analytics.inProgress, value: totals.in_progress, color: COLORS.warning },
      { name: t.analytics.notStarted, value: totals.not_started, color: COLORS.danger },
      { name: t.status.notApplicable, value: totals.not_applicable, color: COLORS.muted }
    ];
  }, [analyticsData, t]);

  // Radar chart data for multi-framework comparison
  const radarData = useMemo(() => {
    const metrics = ['Compliance %', 'Coverage %', 'Implementation %', 'Documentation %', 'Evidence %'];
    return metrics.map(metric => {
      const dataPoint: Record<string, any> = { metric };
      analyticsData.forEach(a => {
        let value = 0;
        switch (metric) {
          case 'Compliance %':
            value = Number(a.dashboard?.stats?.compliance_score) || 0;
            break;
          case 'Coverage %':
            const total = Number(a.dashboard?.stats?.total) || 1;
            const applicable = Number(a.dashboard?.status?.applicable_controls) || 0;
            value = Math.round((applicable / total) * 100);
            break;
          case 'Implementation %':
            const appControls = Number(a.dashboard?.status?.applicable_controls) || 1;
            const implemented = Number(a.dashboard?.stats?.compliant) || 0;
            value = Math.round((implemented / appControls) * 100);
            break;
          case 'Documentation %':
            value = Math.random() * 40 + 60; // Placeholder
            break;
          case 'Evidence %':
            value = Math.random() * 30 + 50; // Placeholder
            break;
        }
        dataPoint[a.framework.code] = value;
      });
      return dataPoint;
    });
  }, [analyticsData]);

  const handleExport = () => {
    const data = {
      generatedAt: new Date().toISOString(),
      overallCompliance,
      frameworks: comparisonData,
      implementationStatus: implementationData,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grc-analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (analyticsData.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 text-center">
        <Activity className="w-16 h-16 mx-auto text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
          {t.analytics.noData}
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          {t.analytics.completeWizard}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            {t.analytics.title}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t.analytics.averageAcross} ({analyticsData.length})
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadAnalytics}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            <RefreshCw className="w-4 h-4" />
            {t.analytics.refresh}
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
          >
            <Download className="w-4 h-4" />
            {t.analytics.export}
          </button>
        </div>
      </div>

      {/* View Selector */}
      <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-fit">
        {[
          { key: 'overview', label: t.analytics.overview, icon: PieChartIcon },
          { key: 'comparison', label: t.analytics.comparison, icon: BarChart3 },
          { key: 'trend', label: t.analytics.maturity, icon: TrendingUp }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setSelectedView(key as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${
              selectedView === key
                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div 
          className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl p-6 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => onNavigate?.('controls')}
        >
          <div className="flex items-center justify-between mb-4">
            <Target className="w-8 h-8 opacity-80" />
            <span className="text-5xl font-bold">{overallCompliance}%</span>
          </div>
          <div className="text-sm opacity-90">{t.analytics.overallCompliance}</div>
          <div className="text-xs opacity-70 mt-1">{t.analytics.averageAcross}</div>
        </div>

        <div 
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => onNavigate?.('overview')}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {analyticsData.length}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.analytics.activeFrameworks}</div>
        </div>

        <div 
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => onNavigate?.('controls', { status: 'compliant' })}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {implementationData[0].value}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.analytics.controlsImplemented}</div>
        </div>

        <div 
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-xl transition-shadow"
          onClick={() => onNavigate?.('controls', { status: 'partial' })}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white">
            {implementationData[1].value + implementationData[2].value}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{t.analytics.pendingActions}</div>
        </div>
      </div>

      {/* Selected Framework Detail */}
      {selectedFramework && (() => {
        const fwAnalytics = analyticsData.find(a => a.framework.id === selectedFramework.id);
        const stats = fwAnalytics?.dashboard?.stats;
        const status = fwAnalytics?.dashboard?.status;
        const total = Number(stats?.total) || Number(selectedFramework.total_controls) || 0;
        const compliant = Number(stats?.compliant) || 0;
        const partial = Number(status?.partial_controls) || 0;
        const notApplicable = Number(status?.not_applicable_controls) || 0;
        const applicable = Number(status?.applicable_controls) || Number(selectedFramework.applicable_controls) || 0;
        const notStarted = Math.max(0, applicable - compliant - partial);
        const fwScore = Number(stats?.compliance_score) || (applicable > 0 ? Math.round((compliant / applicable) * 100) : 0);

        const fwStatusData = [
          { name: t.analytics.implemented, value: compliant, color: COLORS.success },
          { name: t.analytics.inProgress, value: partial, color: COLORS.warning },
          { name: t.analytics.notStarted, value: notStarted, color: COLORS.danger },
          { name: t.status.notApplicable, value: notApplicable, color: COLORS.muted }
        ];

        const maturityLevel = 
          fwScore >= 90 ? { level: 5, label: t.analytics.optimized, color: 'bg-green-500' } :
          fwScore >= 70 ? { level: 4, label: t.analytics.managed, color: 'bg-blue-500' } :
          fwScore >= 50 ? { level: 3, label: t.analytics.defined, color: 'bg-yellow-500' } :
          fwScore >= 30 ? { level: 2, label: t.analytics.developing, color: 'bg-orange-500' } :
          { level: 1, label: t.analytics.initial, color: 'bg-red-500' };

        return (
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border-2 border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-600" />
                  {selectedFramework.code} — {selectedFramework.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {selectedFramework.framework_type} • {total} {t.dashboard.totalControls.toLowerCase()}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${maturityLevel.color}`}>
                  {t.analytics.level} {maturityLevel.level}: {maturityLevel.label}
                </span>
                <span className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{fwScore}%</span>
              </div>
            </div>

            {/* Progress */}
            <div className="mb-6">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden flex">
                {compliant > 0 && <div className="bg-green-500 h-full transition-all" style={{ width: `${(compliant / total) * 100}%` }} title={`${t.analytics.implemented}: ${compliant}`} />}
                {partial > 0 && <div className="bg-yellow-500 h-full transition-all" style={{ width: `${(partial / total) * 100}%` }} title={`${t.analytics.inProgress}: ${partial}`} />}
                {notStarted > 0 && <div className="bg-red-500 h-full transition-all" style={{ width: `${(notStarted / total) * 100}%` }} title={`${t.analytics.notStarted}: ${notStarted}`} />}
                {notApplicable > 0 && <div className="bg-gray-400 h-full transition-all" style={{ width: `${(notApplicable / total) * 100}%` }} title={`${t.status.notApplicable}: ${notApplicable}`} />}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> {t.analytics.implemented}: {compliant}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> {t.analytics.inProgress}: {partial}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> {t.analytics.notStarted}: {notStarted}</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block" /> {t.status.notApplicable}: {notApplicable}</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{total}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.dashboard.totalControls}</div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{applicable}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.dashboard.applicable}</div>
              </div>
              <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{compliant}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.analytics.implemented}</div>
              </div>
              <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-xl">
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">{notStarted + partial}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t.analytics.pendingActions}</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Main Charts */}
      {selectedView === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Implementation Status Pie */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-indigo-600" />
              {t.analytics.globalStatus}
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={implementationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {implementationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Framework Compliance Bar */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              {t.analytics.complianceByFramework}
            </h3>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fill: '#9ca3af' }} />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#f9fafb' }}
                  />
                  <Bar dataKey="compliance" fill={COLORS.primary} radius={[0, 4, 4, 0]} name="Compliance %" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {selectedView === 'comparison' && (
        <div className="space-y-6">
          {/* Stacked Bar Chart - Controls Breakdown */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              {t.analytics.controlsBreakdown}
            </h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af' }} />
                  <YAxis tick={{ fill: '#9ca3af' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                    labelStyle={{ color: '#f9fafb' }}
                  />
                  <Legend />
                  <Bar dataKey="implemented" stackId="a" fill={COLORS.success} name="Implemented" />
                  <Bar dataKey="partial" stackId="a" fill={COLORS.warning} name="Partial" />
                  <Bar dataKey="notApplicable" stackId="a" fill={COLORS.muted} name="N/A" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Radar Chart - Multi-dimension Comparison */}
          {analyticsData.length > 1 && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-indigo-600" />
                {t.analytics.multiDimensional}
              </h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="80%" data={radarData}>
                    <PolarGrid stroke="#374151" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#9ca3af' }} />
                    {analyticsData.map((a, idx) => (
                      <Radar
                        key={a.framework.id}
                        name={a.framework.code}
                        dataKey={a.framework.code}
                        stroke={COLORS.chart[idx % COLORS.chart.length]}
                        fill={COLORS.chart[idx % COLORS.chart.length]}
                        fillOpacity={0.2}
                      />
                    ))}
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedView === 'trend' && (
        <div className="space-y-6">
          {/* Maturity Model */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              {t.analytics.maturityAssessment}
            </h3>
            <div className="space-y-6">
              {comparisonData.map((fw, idx) => {
                const maturityLevel = 
                  fw.compliance >= 90 ? { level: 5, label: t.analytics.optimized, color: 'bg-green-500' } :
                  fw.compliance >= 70 ? { level: 4, label: t.analytics.managed, color: 'bg-blue-500' } :
                  fw.compliance >= 50 ? { level: 3, label: t.analytics.defined, color: 'bg-yellow-500' } :
                  fw.compliance >= 30 ? { level: 2, label: t.analytics.developing, color: 'bg-orange-500' } :
                  { level: 1, label: t.analytics.initial, color: 'bg-red-500' };

                return (
                  <div 
                    key={fw.name} 
                    className="space-y-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-3 rounded-lg -mx-3 transition-colors"
                    onClick={() => {
                      const framework = analyticsData.find(a => a.framework.code === fw.name)?.framework;
                      if (framework && onFrameworkSelect) onFrameworkSelect(framework);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-gray-900 dark:text-white">{fw.name}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({fw.fullName})</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${maturityLevel.color}`}>
                          {t.analytics.level} {maturityLevel.level}: {maturityLevel.label}
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white">{fw.compliance}%</span>
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${maturityLevel.color} transition-all duration-500`}
                        style={{ width: `${fw.compliance}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{fw.implemented} / {fw.applicable} {t.controls.title.toLowerCase()}</span>
                      <span>{fw.total} {t.dashboard.totalControls.toLowerCase()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              {t.analytics.priorityRecommendations}
            </h3>
            <div className="space-y-4">
              {comparisonData
                .filter(fw => fw.compliance < 70)
                .slice(0, 3)
                .map(fw => (
                  <div key={fw.name} className="flex items-start gap-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800">
                    <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{fw.name} - {fw.fullName}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Currently at {fw.compliance}% compliance. 
                        Consider implementing {fw.applicable - fw.implemented - fw.partial} remaining controls 
                        to achieve target compliance level.
                      </div>
                    </div>
                  </div>
                ))}
              {comparisonData.filter(fw => fw.compliance >= 70).length === comparisonData.length && (
                <div className="flex items-center gap-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <div className="text-green-800 dark:text-green-200">
                    All frameworks are at acceptable compliance levels. Continue monitoring and improving.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GRCAnalytics;
